import { useState } from 'react'
import { useUrlShortener } from '../hooks/useUrlShortener.js'

/**
 * ShareButton component that creates a short URL for the current page and allows copying
 */
export default function ShareButton() {
    const { createShortUrl, loading, error } = useUrlShortener()
    const [shortUrl, setShortUrl] = useState(null)
    const [copied, setCopied] = useState(false)

    const handleShare = async () => {
        try {
            // Get the current page URL
            const currentUrl = window.location.href

            // Create short URL
            const result = await createShortUrl(currentUrl)
            setShortUrl(result.shortUrl)
        } catch (err) {
            // Error is handled by the hook
            console.error('Failed to create short URL:', err)
        }
    }

    const handleCopy = async () => {
        if (!shortUrl) return

        try {
            await navigator.clipboard.writeText(shortUrl)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            console.error('Failed to copy URL:', err)
        }
    }

    const handleClose = () => {
        setShortUrl(null)
        setCopied(false)
    }

    return (
        <div className="flex items-center gap-2">
            {!shortUrl ? (
                <button
                    onClick={handleShare}
                    disabled={loading}
                    className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-logo-pink transition hover:bg-pink-50 disabled:opacity-50"
                    aria-label="Share this page"
                >
                    <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                        />
                    </svg>
                    {loading ? 'Creating...' : 'Share'}
                </button>
            ) : (
                <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-sm">
                    <input
                        type="text"
                        value={shortUrl}
                        readOnly
                        className="w-48 bg-transparent text-sm text-gray-700 outline-none"
                        aria-label="Short URL"
                    />
                    <button
                        onClick={handleCopy}
                        className="rounded px-3 py-1 text-sm font-medium text-logo-pink transition hover:bg-pink-50"
                        aria-label="Copy short URL"
                    >
                        {copied ? (
                            <span className="flex items-center gap-1">
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                Copied!
                            </span>
                        ) : (
                            'Copy'
                        )}
                    </button>
                    <button
                        onClick={handleClose}
                        className="rounded px-2 py-1 text-sm text-gray-500 transition hover:bg-gray-100"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
            )}
            {error && (
                <div className="text-sm text-red-600" role="alert">
                    {error}
                </div>
            )}
        </div>
    )
}
